// Copyright © 2021 Move Closer

import { AnyObject, Intersected } from '@movecloser/front-core'

import { ContentSneakPeakData, ImageFile, Identifier, LabelColor } from '../../modules/_contracts'
import { PdfObject } from '../pdf'

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export interface AggregatesRelated {
  /**
   * Returns the current state of the service's related data record.
   */
  getRelated (): RelatedRecord;
  /**
   * Stores related to be able to resolve when needed.
   *
   * @param record
   */
  storeRelated (record: RelatedRecord): void;
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export interface DescriptionOfAuthor extends AnyObject {
  avatar: Related<RelatedType.File>;
  firstName: string;
  id: Identifier;
  lastName: string;
  position?: string;
  description?: string;
  url?: string;
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export interface DescriptionOfContent extends Omit<ContentSneakPeakData, 'author'> {
  author: Related<RelatedType.Author>;
  pdf: PdfObject | null;
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export interface DescriptionOfFile extends AnyObject {
  id: Identifier;
  mime: string;
  m3u8?: string;
  preset: string;
  size: number;
  srcset?: FileSourceSet[];
  type: string;
  url: string;
}

/**
 * @author Olga Milczek <olga.milczek@movecloser.pl>
 */
export interface DescriptionOfLabel extends AnyObject {
  color: LabelColor;
  id: Identifier;
  name: string;
  slug: string;
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export interface DescriptionOfNewsletter extends AnyObject {
  newsletterEndpoint: string;
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export interface DescriptionOfSet extends AnyObject {
  id: Identifier;
  items: Identifier[];
  title: string;
  totalItems: number;
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export interface DescriptionOfGallery extends AnyObject {
  id: Identifier;
  items: ImageFile[];
  title: string;
  totalItems: number;
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export interface DescriptionOfSmartLink extends AnyObject {
  title?: string;
  urlPath: string;
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export interface DescriptionOfTag extends AnyObject {
  id: Identifier;
  name: string;
  properties?: AnyObject;
  url: string;
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export type DescriptionsRecord = Partial<Record<RelatedType, { [key: string]: string }>>

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export interface DriverConfig {
  continueOrder?: boolean;
  overrides?: Override[];
  preventDuplicates?: boolean;
  take?: number;
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export interface FileSourceSet {
  height: number;
  url: string;
  width: number;
}

/**
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 * @author Krzysztof Ustowski
 */
export interface Override {
  /**
   * Index of the element that we want to replace/prepend.
   */
  index: number;

  /**
   * Strategy of the override action.
   */
  strategy: OverrideStrategy;

  /**
   * ID of the related data that should be inserted.
   */
  to: Identifier;
}

/**
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 * @author Krzysztof Ustowski
 */
export enum OverrideStrategy {
  Push = 'push',
  Replace = 'replace'
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export enum Picker {
  Author = 'authorPicker',
  Content = 'contentPicker',
  Gallery = 'galleryPicker',
  Media = 'mediaPicker',
  Set = 'setPicker',
  Snippet = 'snippetPicker',
  Tag = 'tagPicker'
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export type PickerCallback = (
  picker: Picker,
  onSelection: PickerSelectionCallback,
  selected?: Related,
  options?: AnyObject
) => void

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export type PickerSelectionCallback = (selected: never) => void

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export type PossibleTypeDriver = RelatedTypeDriver | Intersected<RelatedTypeDriver, ResolvesRelatedTypeAsync>

/**
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 * @author Krzysztof Ustowski
 */
export interface Related<Type = RelatedType, Value = Identifier> {
  /**
   * Type of the related data.
   */
  type: Type;

  /**
   * ID(s) of the related data.
   */
  value: Value;
}

/**
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 * @author Krzysztof Ustowski
 */
export type RelatedRecord = Partial<Record<RelatedType, RelatedRecordTypeEntries>>

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export interface RelatedRecordTypeEntries {
  [key: string]: AnyObject;
}

/**
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 * @author Krzysztof Ustowski
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export enum RelatedType {
  Author = 'author',
  ExtendedAuthor = 'extended_author',
  Content = 'content',
  DeduplicatedSet = 'list_deduplicated',
  File = 'file',
  Gallery = 'gallery',
  Label = 'label',
  Newsletter = 'newsletter',
  Set = 'list',
  SmartAuthor = 'smartAuthor',
  SmartCategory = 'smartCategory',
  SmartGallery = 'smartGallery',
  SmartTag = 'smartTag',
  Tag = 'tag',
  Snippet = 'snippet',
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export interface RelatedTypeDriver<M = AnyObject, D extends AnyObject = AnyObject> {
  /**
   * Determine if there's a description in the record.
   * @param id
   * @param record
   */
  canDescribe (id: string, record: RelatedRecord): boolean;

  /**
   * Resolve dependent RelatedTypes.
   */
  dependentTypes (): RelatedType[];

  /**
   * Resolves description of related.
   * @param record
   * @param id
   */
  describe (id: string, record: RelatedRecord): D;

  /**
   * Resolves value of related.
   * @param record
   * @param id
   * @param driverResolver
   * @param config
   */
  resolve (id: string, record: RelatedRecord, config: DriverConfig, driverResolver?: ResolvesDriver): M;

  /**
   * Resets driver state.
   */
  reset (): void;
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export type RelatedTypeDriverConstructor = new () => RelatedTypeDriver

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export type RelatedTypeDriverRegistry = Partial<Record<RelatedType, PossibleTypeDriver>>

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export type ResolvesDriver = (type: RelatedType) => PossibleTypeDriver

/**
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export interface ResolvesRelated {
  /**
   * Resolves description of the given related object.
   *
   * @param related
   */
  describe (related: Related): AnyObject;

  /**
   * Reset all drivers state without dumping it's data.
   */
  resetDrivers (): void;

  /**
   * Resolves the actual DATA using the provided type and ID of the related object.
   *
   * @param related - Related object which data we want to resolve.
   * @param [config={}] - Additional config (like e.g. list items' overrides).
   */
  resolve (related: Related, config?: DriverConfig): AnyObject | AnyObject[];
}

export interface ISiteServicePartial {
  getActiveSite (): any;
  getVideoSettings (): VideoSettings;
  getWelcomeFormConfig (): WelcomeFormConfig;
}

export interface VideoSettings {
  youtube: number;
  hls: number;
  on: number;
}

export interface WelcomeFormConfig {
  agreement?: string;
  newsletter?: string;
  banner?: {
    position?: 'left' | 'bottom';
    useAs?: 'ad_banner' | 'image';
    imageUrl?: string;
    redirectExt?: string;
    adData?: string | object;
  };
}

/**
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export interface ResolvesRelatedAsync {
  /**
   * Resolves description of the given related object.
   *
   * @param related
   */
  describe (related: Related): Promise<AnyObject>;

  /**
   * Reset all drivers state without dumping it's data.
   */
  resetDrivers (): void;

  /**
   * Resolves the actual DATA using the provided type and ID of the related object.
   *
   * @param related - Related object which data we want to resolve.
   * @param [config={}] - Additional config (like e.g. list items' overrides).
   */
  resolve (related: Related, config?: DriverConfig): Promise<AnyObject | AnyObject[]>;

  /**
   * Resolves the actual DATA using the provided type and ID of the related object.
   *
   * @param related - Related object which data we want to resolve.
   * @param [config={}] - Additional config (like e.g. list items' overrides).
   */
  resolveSync (related: Related, config?: DriverConfig): AnyObject | null;
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export interface ResolvesRelatedTypeAsync<M extends AnyObject = AnyObject, D extends AnyObject = AnyObject> {
  loadDescription (id: string): Promise<D>;

  loadRelated (id: string): Promise<M>;
}

/**
 * Determines whether the passed-in `input` correctly implements the `Related` interface.
 *
 * @param input - The input to check.
 * @param [type] - The strictly defined `RelatedType` to validate the input against.
 *
 * @typeGuard
 *
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isRelated = (input: any, type?: RelatedType): input is Related => {
  if (typeof input !== 'object' || input === null) {
    return false
  }

  if (
    !Object.prototype.hasOwnProperty.call(input, 'type') ||
    typeof input.type !== 'string' ||
    input.type.length === 0
  ) {
    return false
  }

  if (typeof type !== 'undefined' && input.type !== type) {
    return false
  }

  // noinspection RedundantIfStatementJS
  if (
    !Object.prototype.hasOwnProperty.call(input, 'value') ||
    (typeof input.value !== 'number' && typeof input.value === 'string' && isNaN(parseInt(input.value)))
  ) {
    return false
  }

  return true
}
